import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getReport, getSession } from "@/lib/api";
import { AdminSideNav } from "@/pages/admin/AdminDashboardPage";
import BrandedHeader from "@/components/BrandedHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Brain, ArrowLeft, Download, Flag, CheckCircle, AlertTriangle, Clock, User, Target, Loader2, Eye, Camera, Clipboard, Volume2 } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";

const SCORE_LABELS = {
  technical_knowledge: "Technical Knowledge",
  communication_clarity: "Communication Clarity",
  structured_thinking: "Structured Thinking",
  role_fit_score: "Role Fit",
  scenario_judgement: "Scenario Judgement",
  interpersonal_awareness: "Interpersonal Awareness",
  motivation_and_curiosity: "Motivation & Curiosity",
  proctoring_integrity_score: "Proctoring Integrity",
};

const recColors = {
  ADVANCE: { bg: "bg-[hsl(var(--success))]/15", text: "text-[hsl(var(--success))]", border: "border-[hsl(var(--success))]/25" },
  HOLD: { bg: "bg-[hsl(var(--warning))]/15", text: "text-[hsl(var(--warning))]", border: "border-[hsl(var(--warning))]/25" },
  REJECT: { bg: "bg-[hsl(var(--destructive))]/15", text: "text-[hsl(var(--destructive))]", border: "border-[hsl(var(--destructive))]/25" },
};

export default function AdminReportPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [sessionId]);

  const loadData = async () => {
    try {
      const [reportRes, sessionRes] = await Promise.all([
        getReport(sessionId),
        getSession(sessionId),
      ]);
      setReport(reportRes.data);
      setSession(sessionRes.data);
    } catch (e) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${sessionId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex">
        <AdminSideNav active="Sessions" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-background flex">
        <AdminSideNav active="Sessions" />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Report not found</p>
        </div>
      </div>
    );
  }

  const scores = report.scores || {};
  const rec = report.recommendation || "HOLD";
  const recStyle = recColors[rec] || recColors.HOLD;

  const radarData = Object.entries(SCORE_LABELS).map(([key, label]) => ({
    dimension: label,
    shortName: label.split(' ').map(w => w[0]).join(''),
    value: scores[key] || 0,
    fullMark: 100,
  }));

  const scoreColor = (val) => {
    if (val >= 80) return "text-[hsl(var(--success))]";
    if (val >= 50) return "text-primary";
    return "text-[hsl(var(--destructive))]";
  };

  const proctorIcons = {
    TAB_SWITCH_DETECTED: Eye,
    FACE_NOT_VISIBLE: Camera,
    MULTIPLE_FACES_DETECTED: User,
    CLIPBOARD_PASTE_DETECTED: Clipboard,
    AUDIO_THIRD_PARTY_DETECTED: Volume2,
  };

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSideNav active="Sessions" />
      
      <div className="flex-1 flex flex-col">
        <BrandedHeader className="lg:hidden">
          <h1 className="text-lg font-display font-semibold text-center">Assessment Report</h1>
        </BrandedHeader>

        <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-3 mb-2">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/sessions')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <h1 className="text-2xl font-display font-semibold">Assessment Report</h1>
            </div>

            {/* Candidate Info + Recommendation Banner */}
            <div className={`rounded-xl border ${recStyle.border} ${recStyle.bg} p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4`} data-testid="report-recommendation-banner">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-display font-semibold">{report.candidate_name || 'Unknown'}</h2>
                  <Badge className={`${recStyle.text} ${recStyle.bg} border ${recStyle.border} text-sm font-semibold`}>
                    {rec}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />{report.role || '-'}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{report.duration_minutes || '-'} min</span>
                  <span className="font-mono text-xs">{report.interview_date}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-display font-semibold tabular-nums">{report.overall_score || 0}</p>
                <p className="text-xs text-muted-foreground">Overall Score</p>
              </div>
            </div>

            <Tabs defaultValue="scores" className="space-y-6">
              <TabsList className="bg-secondary">
                <TabsTrigger value="scores">Scores</TabsTrigger>
                <TabsTrigger value="flags">Flags</TabsTrigger>
                <TabsTrigger value="proctoring">Proctoring</TabsTrigger>
                <TabsTrigger value="transcript">Transcript</TabsTrigger>
              </TabsList>

              {/* Scores Tab */}
              <TabsContent value="scores">
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Radar Chart */}
                  <Card className="border-border bg-card" data-testid="report-score-radar">
                    <CardHeader className="p-5">
                      <CardTitle className="text-base font-display">Score Dimensions</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                      <ResponsiveContainer width="100%" height={280}>
                        <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="65%">
                          <PolarGrid stroke="hsl(222 28% 18%)" />
                          <PolarAngleAxis dataKey="shortName" tick={{ fill: 'hsl(215 20% 70%)', fontSize: 10 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: 'hsl(215 16% 60%)', fontSize: 9 }} />
                          <Radar name="Score" dataKey="value" stroke="hsl(199 89% 55%)" fill="hsl(199 89% 55%)" fillOpacity={0.18} strokeWidth={2} />
                          <Tooltip
                            contentStyle={{ background: 'hsl(222 47% 8%)', border: '1px solid hsl(222 28% 18%)', borderRadius: '8px', color: 'hsl(210 40% 98%)', fontSize: '12px' }}
                            formatter={(value, name, props) => [value, props.payload.dimension]}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Score Bars */}
                  <Card className="border-border bg-card" data-testid="report-score-dimensions">
                    <CardHeader className="p-5">
                      <CardTitle className="text-base font-display">Dimension Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-5 space-y-4">
                      {Object.entries(SCORE_LABELS).map(([key, label]) => {
                        const val = scores[key] || 0;
                        return (
                          <div key={key}>
                            <div className="flex justify-between text-sm mb-1.5">
                              <span className="text-muted-foreground">{label}</span>
                              <span className={`font-mono font-semibold tabular-nums ${scoreColor(val)}`}>{val}</span>
                            </div>
                            <Progress value={val} className="h-2" />
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>

                {/* Difficulty Trajectory + Notes */}
                <div className="grid lg:grid-cols-2 gap-6 mt-6">
                  <Card className="border-border bg-card">
                    <CardHeader className="p-5">
                      <CardTitle className="text-base font-display">Difficulty Trajectory</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                      <p className="text-sm text-muted-foreground font-mono">
                        {report.difficulty_trajectory || 'Not tracked'}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-border bg-card">
                    <CardHeader className="p-5">
                      <CardTitle className="text-base font-display">Interviewer Notes</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {report.interviewer_notes || 'No additional notes'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Flags Tab */}
              <TabsContent value="flags">
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Strengths + Green Flags */}
                  <div className="space-y-6">
                    <Card className="border-border bg-card">
                      <CardHeader className="p-5">
                        <CardTitle className="text-base font-display flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-[hsl(var(--success))]" />
                          Strengths
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-5 pb-5">
                        <ul className="space-y-2">
                          {(report.strengths || []).map((s, i) => (
                            <li key={`strength-${i}-${s}`} className="text-sm text-muted-foreground flex items-start gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))] mt-2 flex-shrink-0" />
                              {s}
                            </li>
                          ))}
                          {(!report.strengths || report.strengths.length === 0) && (
                            <p className="text-sm text-muted-foreground">No strengths recorded</p>
                          )}
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="border-border bg-card" data-testid="report-green-flags">
                      <CardHeader className="p-5">
                        <CardTitle className="text-base font-display flex items-center gap-2">
                          <Flag className="w-4 h-4 text-[hsl(var(--success))]" />
                          Green Flags
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-5 pb-5">
                        <ul className="space-y-2">
                          {(report.green_flags || []).map((f, i) => (
                            <li key={`green-${i}-${f}`} className="text-sm text-muted-foreground flex items-start gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))] mt-2 flex-shrink-0" />
                              {f}
                            </li>
                          ))}
                          {(!report.green_flags || report.green_flags.length === 0) && (
                            <p className="text-sm text-muted-foreground">No green flags</p>
                          )}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Development Areas + Red Flags */}
                  <div className="space-y-6">
                    <Card className="border-border bg-card">
                      <CardHeader className="p-5">
                        <CardTitle className="text-base font-display flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-[hsl(var(--warning))]" />
                          Development Areas
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-5 pb-5">
                        <ul className="space-y-2">
                          {(report.development_areas || []).map((d, i) => (
                            <li key={`devarea-${i}-${d}`} className="text-sm text-muted-foreground flex items-start gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--warning))] mt-2 flex-shrink-0" />
                              {d}
                            </li>
                          ))}
                          {(!report.development_areas || report.development_areas.length === 0) && (
                            <p className="text-sm text-muted-foreground">No development areas noted</p>
                          )}
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="border-border bg-card" data-testid="report-red-flags">
                      <CardHeader className="p-5">
                        <CardTitle className="text-base font-display flex items-center gap-2">
                          <Flag className="w-4 h-4 text-[hsl(var(--destructive))]" />
                          Red Flags
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-5 pb-5">
                        <ul className="space-y-2">
                          {(report.red_flags || []).map((f, i) => (
                            <li key={`red-${i}-${f}`} className="text-sm text-muted-foreground flex items-start gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--destructive))] mt-2 flex-shrink-0" />
                              {f}
                            </li>
                          ))}
                          {(!report.red_flags || report.red_flags.length === 0) && (
                            <p className="text-sm text-muted-foreground">No red flags</p>
                          )}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Proctoring Tab */}
              <TabsContent value="proctoring">
                <Card className="border-border bg-card" data-testid="report-proctor-events-timeline">
                  <CardHeader className="p-5">
                    <CardTitle className="text-base font-display">Proctoring Events Timeline</CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-5">
                    {session?.proctoring_events?.length > 0 ? (
                      <div className="space-y-3">
                        {session.proctoring_events.map((evt, i) => {
                          const Icon = proctorIcons[evt.event_type] || AlertTriangle;
                          const isWarning = evt.event_type.includes('TAB') || evt.event_type.includes('PASTE');
                          return (
                            <div key={`${evt.timestamp || ""}-${evt.event_type}-${i}`} className="flex items-start gap-3 py-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isWarning ? 'bg-[hsl(var(--warning))]/15' : 'bg-[hsl(var(--destructive))]/15'
                              }`}>
                                <Icon className={`w-4 h-4 ${isWarning ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--destructive))]'}`} />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{evt.event_type.replace(/_/g, ' ')}</p>
                                <p className="text-xs text-muted-foreground">{evt.details}</p>
                                <p className="text-xs text-muted-foreground font-mono mt-0.5">
                                  {evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString() : ''}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CheckCircle className="w-8 h-8 text-[hsl(var(--success))] mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No proctoring events recorded</p>
                        <p className="text-xs text-muted-foreground mt-1">Clean session</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Transcript Tab */}
              <TabsContent value="transcript">
                <Card className="border-border bg-card" data-testid="report-qa-summary">
                  <CardHeader className="p-5 flex flex-row items-center justify-between">
                    <CardTitle className="text-base font-display">Interview Transcript</CardTitle>
                    <Button variant="ghost" size="sm" onClick={handleDownload}>
                      <Download className="w-3.5 h-3.5 mr-1" /> Export JSON
                    </Button>
                  </CardHeader>
                  <CardContent className="px-5 pb-5">
                    {session?.messages?.length > 0 ? (
                      <div className="space-y-4 max-h-[600px] overflow-y-auto">
                        {session.messages.map((msg, i) => (
                          <div key={`${msg.timestamp || ""}-${msg.role}-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] rounded-xl px-4 py-3 text-sm ${
                              msg.role === 'user'
                                ? 'bg-card border border-primary/20'
                                : 'bg-secondary border border-border'
                            }`}>
                              <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                              <div className="text-xs text-muted-foreground mt-2 font-mono">
                                {msg.role === 'user' ? 'Candidate' : 'AI Interviewer'}
                                {msg.timestamp && ` · ${new Date(msg.timestamp).toLocaleTimeString()}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No transcript available</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
